/** Date helpers matching historical Angular date filters used in templates. */

export function formatTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** "h:mm" + separate period like historical ne__brick__time / ne__period */
export function formatTimeParts(iso?: string | null): {
  time: string;
  period: string;
} {
  if (!iso) return { time: "", period: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: "", period: "" };
  const h = d.getHours();
  const m = d.getMinutes();
  // Angular date filter 'a' → AM/PM (uppercase)
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return {
    time: `${h12}:${String(m).padStart(2, "0")}`,
    period,
  };
}

/** EEE, MMM d — historical brick day (CSS often uppercases). */
export function formatDayShort(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
}

/** mediumDate-ish */
export function formatMediumDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  // plain YYYY-MM-DD from bio
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, mo, day] = iso.split("-").map(Number);
    return new Date(y, mo - 1, day).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Angular inbox style: "Sun, Aug 2 26 at 10:48 AM"
 * (EEE, MMM d yy 'at' h:mm a — no comma between day and year).
 */
export function formatInboxDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = days[d.getDay()];
  const month = months[d.getMonth()];
  const date = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  let h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${day}, ${month} ${date} ${yy} at ${h}:${String(m).padStart(2, "0")} ${period}`;
}

/** M/d/yy — historical campaign log dates */
export function formatShortDate(iso?: string | null): string {
  if (!iso) return "";
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, mo, day] = iso.split("-").map(Number);
    d = new Date(y, mo - 1, day);
  } else {
    d = new Date(iso);
  }
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

export function formatCurrency(amount: number): string {
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

export function titleize(name?: string | null): string {
  if (!name) return "";
  return name
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Historical Angular `tel` filter — (###) ###-#### when possible. */
export function formatPhone(raw?: string | null): string {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return String(raw);
}

/** MM/dd/yy like Angular appointment start dates. */
export function formatApptDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/** Campaign tile type line from historical switch on misc.type. */
export function campaignTypeLabel(campaign: {
  misc?: {
    type?: string;
    job_type?: string;
    verb?: string;
  };
}): string {
  const misc = campaign.misc;
  if (!misc?.type || misc.type === "manual") return "Manual campaign";
  if (misc.type === "when") {
    switch (misc.verb) {
      case "activated":
        return "Sent when a patient is invited to join NexHealth";
      case "confirmed":
        return "Sent when the office confirms an appointment";
      case "rescheduled":
        return "Sent when the office reschedules an appointment";
      case "created":
        return "Sent when the office schedules an appointment";
      case "pulled":
        return "Sent when a waitlist spot opens";
      case "completed":
        return "Sent after the appointment ends";
      default:
        return "Sent when something happens";
    }
  }
  if (misc.type === "schedule") {
    switch (misc.job_type) {
      case "invite":
        return "Sent when a patient's account is being created";
      case "reminder":
        return "Sent before the appointment";
      case "recall":
        return "Sent some time after an appointment";
      case "missed_recall":
        return "Sent when a patient misses their appointment";
      case "cancelled_recall":
        return "Sent when a patient cancels their appointment";
      case "survey":
        return "Sent after the appointment ends";
      default:
        return "Automatic reminders, recalls, invites";
    }
  }
  return "Manual campaign";
}
